import { Functional } from '@wayfinder/vms-base';
import Loki from 'lokijs';
import * as R from 'ramda';

/*
 * LokiDb
 *
 * Get the LokiDb by calling getLoki, which will retrieve/create the singleton
 */

export const ENTITIES = 'entities';
export const FEATURES = 'features';
export const THEME = 'theme';
export const VENUE_SUMMARIES = 'venue-summaries';

let singletonInstance = null;

class LokiDb {
    constructor() {
        this.db = new Loki();
        this.db.addCollection(VENUE_SUMMARIES, { unique: ['id'] });
        this.db.addCollection(FEATURES, { unique: ['id'] });
        this.db.addCollection(ENTITIES, { unique: ['uuid'] });
        this.db.addCollection(THEME, { unique: ['id'] });
    }

    get venueSummaries() {
        return this.db.getCollection(VENUE_SUMMARIES);
    }
    get features() {
        return this.db.getCollection(FEATURES);
    }
    get entities() {
        return this.db.getCollection(ENTITIES);
    }

    /*
     * Insert or update into a collection in Loki
     *
     * @sig upsert :: String, [Object] -> ()
     */
    upsert(collectionName, values) {
        values = R.is(Array, values) ? values : [values];

        const collection = this.db.getCollection(collectionName);
        const uniqueKey = collection.uniqueNames.length ? collection.uniqueNames[0] : 'uuid';

        R.forEach(value => {
            const existing = collection.findOne({ [uniqueKey]: value[uniqueKey] });
            if (existing) collection.update(Object.assign(existing, value));
            else collection.insertOne(value);
        }, values);

        // for memoization purposes
        collection.lastUpdatedTime = Date.now();
    }

    /*
     * Remove one or more values from a collection
     */
    remove(collectionName, values) {
        values = R.is(Array, values) ? values : [values];
        const collection = this.db.getCollection(collectionName);
        const unique = collection.uniqueNames.length ? collection.uniqueNames[0] : 'uuid';
        R.forEach(value => collection.findAndRemove({ [unique]: value[unique] }), values);
    }

    /*
     * Find the terminals for the venue
     *
     * @sig findTerminalsForVenue :: String -> [String]
     */
    findTerminalsForVenue(venueId) {
        const terminals = R.pluck('terminal', this.entities.find({ venueId }));
        return uniqWithoutNil(terminals);
    }

    /*
     * Find the levels for the terminal
     *
     * @sig findLevelsForTerminal :: (String, String) -> [String]
     */
    findLevelsForTerminal(venueCode, terminal) {
        const floors = R.pluck('floor', this.entities.find({ venueId: venueCode, terminal }));
        return uniqWithoutNil(floors);
    }

    /*
     * Find the levels for the terminal
     *
     * @sig findLevelsForTerminal :: (String, String) -> [String]
     */
    findSpriteOptionsForVenue(venueCode) {
        const features = this.features.find({ 'properties.venueId': venueCode });
        const getPath = path => feature => R.path(path, feature);

        const logos = R.reject(R.isEmpty, R.map(getPath(['properties', 'logo']), features));
        const categories = R.reject(R.isEmpty, R.map(getPath(['properties', 'category']), features));

        return uniqWithoutNil(R.concat(categories, logos));
    }

    /*
     * Filters the Entity list by the search text
     *
     * @sig searchEntities :: (String, String) -> [Entity]
     */
    searchEntities(venueId, searchText) {
        if (!searchText) return this.entities.find({ venueId });

        const powerSearchText = splitSearchText(searchText);
        const powerMatches = createSearchObjects(powerSearchText);
        const search = R.mergeRight(powerMatches, { venueId });
        return this.entities.find(search);
    }

    getNextId(venueId, collectionName, findProp, sortProp) {
        const collection = this.db.getCollection(collectionName);
        const result = collection
            .chain()
            .find({ [findProp]: venueId })
            .simplesort(sortProp, true)
            .limit(1)
            .data();

        if (result.length === 1) {
            const id = Number(R.path(sortProp.split('.'), result[0]));
            return id + 1;
        }
        return 'INVALID_ID';
    }
}

const uniqWithoutNil = a => R.reject(R.isNil, R.uniq(a));

// returns a singleton instance of the LokiDb class. Use the singleton throughout the app
function getLoki() {
    if (singletonInstance === null) {
        singletonInstance = new LokiDb();
    }
    return singletonInstance;
}

// ---------------------------------------------------------------------------------------------------------------------
//
// Powersearch helpers
//
// ---------------------------------------------------------------------------------------------------------------------

// @sig splitSearchText :: String -> [String]
const splitSearchText = searchText => {
    const regex = /(?:[^\s"']+"([^"]+)")|(?:[^\s"']+'([^']+)')|(?:[^\s"']+"([^"]+))|(?:[^\s"']+'([^']+))|(?:[^\s"']+)/g;
    const regexMatches = searchText.trim().match(regex);
    return regexMatches ? R.map(str => str.replace(/['"]/g, ''), regexMatches) : '';
};

/*
 * Combines objects into one for Loki to perform an AND search on
 * @sig and :: [String] -> Object
 */
const createSearchObjects = searchText => {
    const allMatchObjects = R.map(evaluateSearch, searchText);
    return R.mergeAll(allMatchObjects);
};

/*
 * Checks if the search text contains a ':' for a power search
 * @sig evaluateSearch :: String -> Object
 */
const evaluateSearch = searchText => {
    const splitText = searchText.split(':');
    return splitText.length > 1 ? getObjectMatches(splitText) : getNameMatches(splitText[0]);
};

/*
 * Return an object for Loki to perform a search on
 * @sig getMatches :: [String] -> Object
 */
const getObjectMatches = searchText => {
    const [type, property] = searchText;
    const regex = escapeText(property);
    switch (type) {
        case 'id':
            return getIdMatches(Number(property));
        case 'category':
            return getCategoryMatches(regex);
        case 'status':
            return getStatusMatches(regex);
        case 'location':
            return getLocationMatches(regex);
        case 'level':
            return getLevelMatches(regex);
        case 'building':
            return getBuildingMatches(regex);
        case 'tag':
            return getTags(regex);
        default:
            return {};
    }
};

/* Return the name object for Loki to perform a search on
 * @sig getNameMatches :: String -> Object
 */
const getNameMatches = searchText => getNames(escapeText(searchText));

// @sig escapeText :: String -> String
const escapeText = searchText => {
    const escapedSearchText = searchText.replace(/[-[\]'"{}()*+?.,\\^$|#\s]/g, '\\$&');
    return new RegExp(`\\b${escapedSearchText}`, 'i');
};

/*
 * Returns an Object for Loki to perform a search on
 * @sig get :: property -> Object
 */
const getBuildingMatches = terminal => ({ terminal: { $regex: terminal } });
const getCategoryMatches = category => ({ category: { $regex: category } });
const getIdMatches = numericId => ({ numericId });
const getLevelMatches = floor => ({ floor: { $regex: floor } });
const getLocationMatches = gate => ({ gate: { $regex: gate } });
const getNames = regex => ({ name: { $regex: regex } });
const getTags = tags => ({ $or: [{ tags: { $regex: tags } }, { displayTags: { $regex: tags } }] });
const getStatusMatches = status =>
    status.test('open') ? { id: { $regex: /^[0-9]+/ } } : { id: { $regex: /#[0-9]+/ } };

// ---------------------------------------------------------------------------------------------------------------------
//
// API
//
// ---------------------------------------------------------------------------------------------------------------------

export const upsert = (collectionName, values, loki = getLoki()) => loki.upsert(collectionName, values);
export const remove = (collectionName, values, loki = getLoki()) => loki.remove(collectionName, values);

export const removeFeature = (feature, loki = getLoki()) => remove('features', feature, loki);
export const removeEntity = (entity, loki = getLoki()) => remove('entities', entity, loki);

// ---------------------------------------------------------------------------------------------------------------------
//
// SELECTORS
//
// ---------------------------------------------------------------------------------------------------------------------

// @sig getVenueSummary :: (VenueId, lokiDb) -> VenueSummary|undefined
export const getVenueSummary = (venueId, lokiDb = getLoki()) => lokiDb.venueSummaries.findOne({ id: venueId });

// @sig getVenueSummaries :: (lokiDb) -> [VenueSummary]
export const getVenueSummaries = (lokiDb = getLoki()) => lokiDb.venueSummaries.chain().data();

export const searchVenueSummaries = (searchText, translatedVenues = false, lokiDb = getLoki()) => {
    if (!searchText && translatedVenues) return lokiDb.venueSummaries.find();
    if (!searchText) return lokiDb.venueSummaries.find({ locale: 'en' });

    const escapedSearchText = searchText.replace(/[-[\]'"{}()*+?.,\\^$|#\s]/g, '\\$&');
    const ignoreCase = new RegExp(escapedSearchText, 'gi');

    return lokiDb.venueSummaries.find({
        $or: [
            { name: { $regex: ignoreCase } },
            { id: { $regex: ignoreCase } },
            { airportCode: { $regex: ignoreCase } },
        ],
    });
};

export const getFeatures = (lokiDb = getLoki()) => lokiDb.features.chain().data();
export const getEntities = (lokiDb = getLoki()) => lokiDb.entities.chain().data();

export const getFeature = (id, lokiDb = getLoki()) => lokiDb.features.findOne({ id });

export const getHasBadgeList = (lokiDb = getLoki()) => lokiDb.categories.find({ hasBadge: true });

export const getSpritesForVenue = (venueId, lokiDb = getLoki()) => lokiDb.findSpriteOptionsForVenue(venueId);

const paramsToString = (venueId, searchText, lokiDb = getLoki()) =>
    JSON.stringify({
        venueId,
        searchText,
        lastUpdatedTime: lokiDb.entities.lastUpdatedTime,
    });

const _searchEntities = (venueId, searchText, lokiDb = getLoki()) => {
    return lokiDb.searchEntities(venueId, searchText);
};
export const searchEntities = Functional.memoizeOnceWith(paramsToString, _searchEntities);

export const findEntity = (conditions, lokiDb = getLoki()) => lokiDb.entities.findOne(conditions);

/*
 * Return an object where each key is a tag and each value is the count of Entities having that tag
 * @sig tagCountsForVenue :: VenueId -> { Tag: Number }
 *      Tag = String
 */
const tagCountsForVenue = (venueId, lokiDb = getLoki()) => {
    // :: Entity -> [Tag]
    const allTagsForEntity = ({ tags = [], displayTags = [] }) => R.concat(tags, displayTags);

    const entities = lokiDb.entities.find({ venueId }); // -> [Entity]
    const tags = R.chain(allTagsForEntity, entities); // -> [Tag]
    return R.countBy(R.identity, tags); // -> { Tag: Number }
};

let lastTagTuples;
let lastEntityUpdatedTime = -999;

/*
 * Return an array of label/value tuples suitable for a dropdown list. Each label includes the number of occurrences:
 *
 *    { value: 'bakery', label: 'bakery (4)' }
 *
 * Memoized for your convenience
 * @sig buildTagTuples :: VenueId -> [TagTuple]
 *      Tag      = String
 *      TagTuple = { value: Tag, label: String }
 */
export const buildTagTuples = (venueId, lokiDb = getLoki()) => {
    // :: [Tag, Number] -> TagTuple
    const pairToTuple = pair => ({ value: pair[0], label: `${pair[0]} (${pair[1]})` });

    // select the head of a pair to sort on :: [Tag, Number] -> String
    const pairHead = R.pipe(R.head, R.toLower);

    if (lastEntityUpdatedTime !== lokiDb.entities.lastUpdatedTime) {
        lastEntityUpdatedTime = lokiDb.entities.lastUpdatedTime;

        const tagCounts = tagCountsForVenue(venueId); // -> { Tag: Number }
        const pairs = R.toPairs(tagCounts); // -> [[Tag, Number]]
        const sortedPairs = R.sortBy(pairHead, pairs); // -> [[Tag, Number]]
        lastTagTuples = R.map(pairToTuple, sortedPairs); // -> [TagTuple]
    }

    return lastTagTuples;
};

export const findTerminalsForVenue = (venueId, lokiDb = getLoki()) => lokiDb.findTerminalsForVenue(venueId);

export const getNextEntityId = (venueId, lokiDb = getLoki()) =>
    lokiDb.getNextId(venueId, ENTITIES, 'venueId', 'numericId');
export const getNextFeatureId = (venueId, lokiDb = getLoki()) =>
    lokiDb.getNextId(venueId, FEATURES, 'properties.venueId', 'properties.id');
export const getNextId = (venueId, collectionName, findProp, sortProp, lokiDb = getLoki()) =>
    lokiDb.getNextId(venueId, collectionName, findProp, sortProp);

export const findLevelsForTerminal = (venueId, terminalName, lokiDb = getLoki()) =>
    lokiDb.findLevelsForTerminal(venueId, terminalName);

/*
 * clear the collections
 * impure: removes all the data!
 * @sig reset :: () -> ()
 */
export const reset = () => {
    const lokiDb = getLoki();
    R.forEach(c => c.clear(), lokiDb.db.collections);
};
